import {useEffect, useState} from 'react';
import {useMutation} from '@apollo/client';
import {Button, Popconfirm, Switch} from 'antd';
import DBModal from 'components/modal/modal';
import {toast} from 'react-toastify';
import styles from '../subscriptionSettings.module.scss';

const SubscriptionResourceStatusModal = ({
  isOpen,
  onClose,
  resourceData,
  refetch,
  canSubmit,
  mutation,
  mutationName,
  successTypename,
  resourceLabel = 'Resource',
}) => {
  const currentEnabled = Boolean(resourceData?.isEnabled);
  const [isEnabled, setIsEnabled] = useState(currentEnabled);

  const [changeStatus, {loading}] = useMutation(mutation, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setIsEnabled(Boolean(resourceData?.isEnabled));
  }, [isOpen, resourceData]);

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    if (!resourceData?.key) {
      toast.error(`Missing ${resourceLabel.toLowerCase()} key`);
      return;
    }

    changeStatus({
      variables: {
        input: {
          key: resourceData.key,
          isEnabled: Boolean(isEnabled),
        },
      },
    })
      .then(({data}) => {
        const response = data?.[mutationName];

        if (response?.__typename === 'Error') {
          toast.error(response.message);
          return;
        }

        if (successTypename && response?.__typename !== successTypename) {
          toast.error(response?.message || 'Unexpected response');
          return;
        }

        toast.success(
          response?.message || `${resourceLabel} status updated successfully`,
        );
        handleClose();
        if (refetch) {
          refetch();
        }
      })
      .catch(error => {
        toast.error(
          error.message ||
            `Failed to update ${resourceLabel.toLowerCase()} status`,
        );
      });
  };

  const hasChanged = currentEnabled !== Boolean(isEnabled);
  const nextAction = isEnabled ? 'enable' : 'disable';

  return (
    <DBModal
      isOpen={isOpen}
      handleClose={handleClose}
      destroyOnClose
      width={560}
      content={
        <div className={styles['status-modal']}>
          <div className={styles['status-modal-header']}>
            <h1>Change {resourceLabel} Status</h1>
            <p>
              Toggle availability for this {resourceLabel.toLowerCase()}.
              Changes are applied immediately after confirmation.
            </p>
          </div>

          <div className={styles['status-plan-card']}>
            <div className={styles['status-plan-row']}>
              <div>
                <p className={styles['status-plan-label']}>{resourceLabel}</p>
                <p className={styles['status-plan-name']}>
                  {resourceData?.name || '-'}
                </p>
              </div>
            </div>

            <div className={styles['status-toggle-row']}>
              <div className={styles['status-toggle-copy']}>
                <strong>{isEnabled ? 'Enabled' : 'Disabled'}</strong>
                <span>
                  {isEnabled
                    ? `${resourceLabel} is available for users.`
                    : `${resourceLabel} is hidden and cannot be used.`}
                </span>
              </div>
              <Switch
                checked={isEnabled}
                onChange={checked => setIsEnabled(checked)}
                disabled={loading || !canSubmit}
              />
            </div>
          </div>

          <div className={styles['status-modal-actions']}>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Popconfirm
              title="Confirm status change"
              description={`Are you sure you want to ${nextAction} this ${resourceLabel.toLowerCase()}?`}
              okText="Yes"
              cancelText="No"
              onConfirm={handleConfirm}
              disabled={loading || !canSubmit || !hasChanged}>
              <Button
                type="primary"
                loading={loading}
                disabled={!canSubmit || !hasChanged}>
                Update Status
              </Button>
            </Popconfirm>
          </div>
        </div>
      }
    />
  );
};

export default SubscriptionResourceStatusModal;
